import { isAxiosError } from 'axios';

// The api's 422 body, as ApplicationController's `rescue_from ActiveRecord::RecordInvalid`
// renders it: `{ errors: { <field>: [message, ...] } }`, camelized on the way out by
// OliveBranch. `base` is Rails' record-level key — a message with no field to name.
const RECORD_LEVEL_KEY = 'base';

// Words the api's field names carry that read wrong sentence-cased.
const ACRONYMS = new Set(['id', 'url', 'ssn', 'mrn', 'npi', 'api', 'snf']);

// `primaryId` is not what the form calls that control, but it is closer to it than
// "Something went wrong" — and a form that cares passes its own label through
// `fieldLabels`. Without a subject the api's messages read as fragments: they are
// predicates ("must be an enabled retrieval field"), written to follow a field name.
export function humanizeFieldName(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      // Plurals too: the collection keys admin forms post are `<thing>Ids` (portalSystemIds,
      // facilityGroupIds), so this is the common shape rather than the exotic one.
      if (lower.endsWith('s') && ACRONYMS.has(lower.slice(0, -1))) return `${lower.slice(0, -1).toUpperCase()}s`;
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

// The first usable message under an api error key. Exported because a form reading the body
// for itself needs exactly this shape check — see EditClient's saveErrorMessage.
export function firstMessage(value: unknown): string | undefined {
  return Array.isArray(value) && typeof value[0] === 'string' && value[0].length > 0 ? value[0] : undefined;
}

// The api's own account of a rejected save, ready to toast. Only the FIRST field is
// reported: a 422 usually carries one, and a toast is the wrong surface for a list.
// Returns undefined for anything that is not a field rejection — a network failure or a
// 500 says nothing an admin can act on, so those keep the caller's generic message.
export default function apiFieldError(error: unknown, fieldLabels?: Record<string, string>): string | undefined {
  if (!isAxiosError(error) || error.response?.status !== 422) return undefined;

  const errors = error.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return undefined;

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    const message = firstMessage(value);
    if (!message) continue;
    if (key === RECORD_LEVEL_KEY) return message;

    return `${fieldLabels?.[key] ?? humanizeFieldName(key)}: ${message}`;
  }

  return undefined;
}
